/**
 * Provenance-aware write helpers.
 *
 * Routes writes to the correct store action based on whether the
 * entity originated from a blend file or was user-created.
 */

import * as THREE from "three/webgpu";
import { findEntryByObjectName } from "../app/blendFileQueries.ts";
import { getCollectionForObject } from "../app/collectionGraph.ts";
import { decomposeWorldToLocalTransform } from "../app/groupTransform.ts";
import { useStore } from "../app/store.ts";
import type { EntityId } from "./EntityId.ts";
import { blendNameFromId, isBlendEntityId } from "./EntityId.ts";

// Scratch objects — reused across calls to avoid allocation.
const _v3 = new THREE.Vector3();
const _groupQ = new THREE.Quaternion();
const _localQ = new THREE.Quaternion();
const _worldQ = new THREE.Quaternion();
const _euler = new THREE.Euler();

/**
 * Update an entity's transform, routing to the correct store action.
 * For blend objects, decomposes the world-space transform back into a
 * group transform by factoring out the object's local transform.
 */
export function updateEntityTransform(
  entityId: EntityId,
  position: [number, number, number],
  rotation: [number, number, number],
  scale: [number, number, number],
): void {
  const store = useStore.getState();
  if (isBlendEntityId(entityId)) {
    const name = blendNameFromId(entityId);
    const found = findEntryByObjectName(store.blendFiles, name);
    if (found) {
      const [entryId, entry] = found;
      const obj = entry.file.objects.find((o) => o.name === name);

      if (obj) {
        // Decompose: groupTransform = worldTransform * inverse(localTransform)

        // Rotation: groupRot = worldRot * inverse(localRot)
        _worldQ.setFromEuler(_euler.set(rotation[0], rotation[1], rotation[2]));
        _localQ.setFromEuler(
          _euler.set(
            obj.localRotation[0],
            obj.localRotation[1],
            obj.localRotation[2],
          ),
        );
        _groupQ.copy(_worldQ).multiply(_localQ.invert());
        _groupQ.normalize();

        // Scale: groupScale = worldScale / localScale
        const gScale: [number, number, number] = [
          obj.localScale[0] !== 0 ? scale[0] / obj.localScale[0] : scale[0],
          obj.localScale[1] !== 0 ? scale[1] / obj.localScale[1] : scale[1],
          obj.localScale[2] !== 0 ? scale[2] / obj.localScale[2] : scale[2],
        ];

        // Position: groupPos = worldPos - groupRot * (groupScale * localPos)
        _v3.set(
          gScale[0] * obj.localPosition[0],
          gScale[1] * obj.localPosition[1],
          gScale[2] * obj.localPosition[2],
        );
        _v3.applyQuaternion(_groupQ);
        const gPos: [number, number, number] = [
          position[0] - _v3.x,
          position[1] - _v3.y,
          position[2] - _v3.z,
        ];

        store.updateGroupTransform(entryId, {
          position: gPos,
          rotation: [_groupQ.x, _groupQ.y, _groupQ.z, _groupQ.w],
          scale: gScale,
        });
      } else {
        // Fallback: object not found in entry, set group transform directly
        const q = new THREE.Quaternion().setFromEuler(
          new THREE.Euler(...rotation, "XYZ"),
        );
        store.updateGroupTransform(entryId, {
          position,
          rotation: [q.x, q.y, q.z, q.w],
          scale,
        });
      }
    }
  } else {
    const collection = getCollectionForObject(store.collections, entityId);
    if (collection) {
      const local = decomposeWorldToLocalTransform(collection.transform, {
        position,
        rotation,
        scale,
      });
      store.updateSceneObjectTransform(
        entityId,
        local.position,
        local.rotation,
        local.scale,
      );
      return;
    }
    store.updateSceneObjectTransform(entityId, position, rotation, scale);
  }
}
